/**
 * @file: MessageReactionPill.tsx
 *
 * The quick-reaction capsule that floats above the lifted bubble in the
 * long-press overlay — a curated emoji set + a chevron that expands into the
 * full emoji grid (Telegram/iMessage style). Tapping an emoji toggles it on
 * the message; the viewer's already-applied reactions are tinted. Pure: the
 * overlay supplies the toggle + expand handlers + which emojis the viewer has
 * reacted with.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChevronDown } from 'lucide-react-native';
import { FC, memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

/** The curated quick-reaction set shown collapsed. */
export const QUICK_REACTIONS = ['👍', '❤️', '👎', '🔥', '🥰', '👏', '😄'];

export const MessageReactionPill: FC<{
  /** Emojis the viewer has already reacted with (tinted as selected). */
  myReacted: Set<string>;
  onReact: (emoji: string) => void;
  /** Expand into the full emoji grid. */
  onExpand: () => void;
}> = memo(({ myReacted, onReact, onExpand }) => {
  const { rpx } = useResponsiveContext();
  const btnSize = rpx(30);
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#ffffff',
        borderRadius: rpx(24),
        paddingHorizontal: rpx(6),
        paddingVertical: rpx(5),
        gap: rpx(2),
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.18,
        shadowRadius: 16,
        elevation: 8,
      }}
    >
      {QUICK_REACTIONS.map(emoji => (
        <TouchableOpacity
          key={emoji}
          activeOpacity={0.6}
          onPress={() => onReact(emoji)}
          style={{
            paddingHorizontal: rpx(5),
            paddingVertical: rpx(3),
            borderRadius: rpx(16),
            backgroundColor: myReacted.has(emoji) ? '#dbeafe' : 'transparent',
          }}
        >
          <Typography.Text size={rpx(24)} lineHeight={rpx(32)}>
            {emoji}
          </Typography.Text>
        </TouchableOpacity>
      ))}
      <TouchableOpacity
        activeOpacity={0.6}
        onPress={onExpand}
        style={{
          width: btnSize,
          height: btnSize,
          borderRadius: btnSize / 2,
          marginLeft: rpx(2),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#e5e7eb',
        }}
      >
        <ChevronDown color="#6b7280" size={rpx(18)} strokeWidth={2.5} />
      </TouchableOpacity>
    </View>
  );
});
